class InfiniteCarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.flexible-carousel-infinite__track');
    this.slides = this.querySelectorAll('.flexible-carousel-infinite__slide');

    if (!this.track || this.slides.length === 0) return;

    this.scrollPosition = 0;
    this.scrollSpeed = parseFloat(this.dataset.scrollSpeed) || 0.5; // pixels per frame
    this.isPaused = false;
    this.animationID = null;

    this.init();
  }

  init() {
    // Clone slides for infinite loop
    this.cloneSlides();

    // Create fade edges
    this.createFadeEdges();

    // Pause on hover/focus
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.resume());

    // Handle touch - pause while touching
    this.track.addEventListener('touchstart', () => this.pause(), { passive: true });
    this.track.addEventListener('touchend', () => this.resume());

    // Resize observer to recalculate on size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.calculateTrackWidth();
    });
    this.resizeObserver.observe(this);

    // Calculate initial track width
    this.calculateTrackWidth();

    // Start the infinite scroll animation
    this.startAnimation();
  }

  cloneSlides() {
    // Clone all slides and append them for seamless looping
    this.slides.forEach(slide => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('flexible-carousel-infinite__slide--clone');
      this.track.appendChild(clone);
    });
  }

  calculateTrackWidth() {
    // Get the width of original slides (not clones)
    const originalSlides = this.track.querySelectorAll('.flexible-carousel-infinite__slide:not(.flexible-carousel-infinite__slide--clone)');
    if (originalSlides.length === 0) return;

    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    let totalWidth = 0;

    originalSlides.forEach(slide => {
      totalWidth += slide.offsetWidth + gap;
    });

    this.originalWidth = totalWidth;
  }

  createFadeEdges() {
    // Create left fade edge
    const leftFade = document.createElement('div');
    leftFade.className = 'flexible-carousel__fade flexible-carousel__fade--left';
    leftFade.style.cssText = `
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 80px;
      background: linear-gradient(to right, var(--carousel-fade-color, #ffffff) 0%, transparent 100%);
      pointer-events: none;
      z-index: 2;
    `;

    // Create right fade edge
    const rightFade = document.createElement('div');
    rightFade.className = 'flexible-carousel__fade flexible-carousel__fade--right';
    rightFade.style.cssText = `
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 80px;
      background: linear-gradient(to left, var(--carousel-fade-color, #ffffff) 0%, transparent 100%);
      pointer-events: none;
      z-index: 2;
    `;

    // Ensure carousel has relative positioning for absolute children
    this.style.position = 'relative';
    this.style.overflow = 'hidden';

    this.appendChild(leftFade);
    this.appendChild(rightFade);
  }

  startAnimation() {
    const animate = () => {
      if (!this.isPaused) {
        this.scrollPosition += this.scrollSpeed;

        // Reset position when we've scrolled past the original content
        if (this.scrollPosition >= this.originalWidth) {
          this.scrollPosition = 0;
        }

        this.track.style.transform = `translateX(-${this.scrollPosition}px)`;
      }

      this.animationID = requestAnimationFrame(animate);
    };

    this.animationID = requestAnimationFrame(animate);
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationID) {
      cancelAnimationFrame(this.animationID);
    }
  }
}

customElements.define('infinite-carousel-component', InfiniteCarouselComponent);
